import React from "react";
import { SummaryType } from "@ls/grouping-service";
import { exchange, useQuery } from "@ls/js-fetch";
import { titleCase } from "@ls/js-utils";
import { useDispatch, useSelector } from "react-redux";
import { DELETE_DEFINITION } from "./actions";
import CalcType from "state/Constants/CalcType";
import ScaleType from "state/Constants/ScaleType";
import {
  selectDefinitions,
  selectFetchingData,
  selectFetchingRatioData,
  selectFetchingSbsData,
} from "./selectors";
import { selectSideBySideValue } from "state/selections/selectors";
import SelectionState from "state/selections/SelectionState";

export const useRatingOptionsQuery = () => {
  return useQuery(["ratings"], async () => {
    const result = await exchange({
      baseURL: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
      url: `qualityRatingFields?appName=${process.env.REACT_APP_ID}`,
    });
    const ratingOptions = result?.data ?? [];
    return {
      ratingOptions,
      ratingOptionsByRatingId: ratingOptions.reduce((map, ratingOption) => {
        map[ratingOption.id] = ratingOption;
        return map;
      }, {}),
    };
  });
};

export const useColumnDefs = () => {
  const definitions = useSelector(selectDefinitions(SelectionState.COMMITTED));
  const fetchingAbsData = useSelector(selectFetchingData(SummaryType.ABSOLUTE));
  const fetchingRelData = useSelector(selectFetchingData(SummaryType.RELATIVE));
  const fetchingRelRatioData = useSelector(
    selectFetchingRatioData(SummaryType.RELATIVE),
  );
  const fetchingAbsSbsData = useSelector(
    selectFetchingSbsData(SummaryType.ABSOLUTE),
  );
  const fetchingRelSbsData = useSelector(
    selectFetchingSbsData(SummaryType.RELATIVE),
  );

  const sideBySideValue = useSelector(
    selectSideBySideValue(SelectionState.COMMITTED),
  );
  const dispatch = useDispatch();
  return React.useMemo(() => {
    return definitions.reduce((columnDefs, definition) => {
      const { ratingId, scaleType, calcType, key, isText, isNumeric } =
        definition;
      const prefix =
        scaleType === ScaleType.Contribution
          ? "C-"
          : String(scaleType) === "25" || scaleType === ScaleType[25]
            ? "P25-"
            : String(scaleType) === "50" || scaleType === ScaleType[50]
              ? "P50-"
              : String(scaleType) === "75" || scaleType === ScaleType[75]
                ? "P75-"
                : scaleType === ScaleType.Ratio
                  ? "Ratio-"
                  : "";
      const numericColumnDef = {
        field: `${key}-numeric`,
        colId: `${key}-numeric`,
        X_onDelete: () =>
          dispatch({ type: DELETE_DEFINITION, payload: definition }),
      };
      const textColumnDef = {
        field: `${key}-text`,
        colId: `${key}-text`,
        X_onDelete: () =>
          dispatch({ type: DELETE_DEFINITION, payload: definition }),
        comparator: function (valueA, valueB, nodeA, nodeB) {
          let valA = nodeA ? nodeA.data[numericColumnDef.field] : valueA;
          let valB = nodeB ? nodeB.data[numericColumnDef.field] : valueB;
          if (valA === 0 || valA === undefined) {
            //sometimes ratings are 0 or null. treat these as same
            valA = null;
          }
          if (valB === 0 || valB === undefined) {
            valB = null;
          }
          if (valA === valB) {
            return 0;
          }
          if (!valA) {
            return -1;
          }
          if (!valB) {
            return 1;
          }
          return valA > valB ? 1 : -1;
        },
      };

      switch (calcType) {
        case CalcType.Account:
          numericColumnDef.headerName = `Acct ${prefix}${titleCase(ratingId)}`;
          numericColumnDef.loading = fetchingAbsData;
          textColumnDef.headerName = `Acct ${prefix}${titleCase(ratingId)}`;
          textColumnDef.loading = fetchingAbsData;
          break;
        case CalcType.Relative:
          numericColumnDef.headerName = `Rel ${prefix}${titleCase(ratingId)}`;
          numericColumnDef.loading =
            scaleType === ScaleType.Ratio
              ? fetchingRelRatioData
              : fetchingRelData;
          textColumnDef.headerName = `Rel ${prefix}${titleCase(ratingId)}`;
          textColumnDef.loading =
            scaleType === ScaleType.Ratio
              ? fetchingRelRatioData
              : fetchingRelData;
          break;
        case CalcType.Benchmark:
          numericColumnDef.headerName = `Bench ${prefix}${titleCase(ratingId)}`;
          numericColumnDef.loading = fetchingAbsData;
          textColumnDef.headerName = `Bench ${prefix}${titleCase(ratingId)}`;
          textColumnDef.loading = fetchingAbsData;
          break;
        default:
          throw Error("Unknown calc type: " + calcType);
      }
      const sbsNumericColDef = { ...numericColumnDef };
      const sbsTextColDef = { ...textColumnDef };
      if (sideBySideValue) {
        sbsNumericColDef.colId += " - scenario";
        sbsNumericColDef.field += " - scenario";
        sbsNumericColDef.headerName += " - scenario";
        sbsTextColDef.colId += " - scenario";
        sbsTextColDef.field += " - scenario";
        sbsTextColDef.headerName += " - scenario";
        switch (calcType) {
          case CalcType.Account:
            sbsNumericColDef.loading = fetchingAbsSbsData;
            sbsTextColDef.loading = fetchingAbsSbsData;
            break;
          case CalcType.Relative:
            sbsNumericColDef.loading = fetchingRelSbsData;
            sbsTextColDef.loading = fetchingRelSbsData;
            break;
          case CalcType.Benchmark:
            sbsNumericColDef.loading = fetchingAbsSbsData && fetchingRelSbsData;
            sbsTextColDef.loading = fetchingAbsSbsData && fetchingRelSbsData;
            break;
          default:
            throw Error("Unknown calc type: " + calcType);
        }
        sbsNumericColDef.X_sbs = true;
        sbsNumericColDef.X_originalColId = key;
        sbsNumericColDef.X_backgroundColor = "#fffb95";
        sbsTextColDef.X_sbs = true;
        sbsTextColDef.X_originalColId = key;
        sbsTextColDef.X_backgroundColor = "#fffb95";
        if (isNumeric) {
          columnDefs.push(sbsNumericColDef);
        }
        if (isText) {
          columnDefs.push(sbsTextColDef);
        }
      }
      if (isNumeric) {
        columnDefs.push(numericColumnDef);
      }
      if (isText) {
        columnDefs.push(textColumnDef);
      }
      return columnDefs;
    }, []);
  }, [
    dispatch,
    definitions,
    fetchingAbsData,
    fetchingRelData,
    fetchingRelRatioData,
    sideBySideValue,
    fetchingAbsSbsData,
    fetchingRelSbsData,
  ]);
};
